import { useEffect, useState } from "react";
import { X, PaperPlaneRight, CalendarBlank } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { fmtDue } from "./InboxThread";

const FIELD = "w-full h-10 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#1E5FEB] focus:ring-2 focus:ring-[#DBEAFE]";
const LABEL = "block text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5";

export function InboxCompose({ open, onClose, onSent, isAdmin, ticket, defaultKind = "message", defaultTo = "" }) {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ to_id: defaultTo, kind: defaultKind, text: "", due_at: "", ticket_no: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ to_id: defaultTo, kind: defaultKind, text: "", due_at: "", ticket_no: ticket?.ticket_number || "" });
    api.get("/inbox/staff").then((r) => setStaff(r.data)).catch(() => {});
  }, [open, defaultKind, defaultTo, ticket]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.to_id || !form.text.trim()) return toast.error("Pick a teammate and write a message");
    setBusy(true);
    try {
      let ticket_id = ticket?.id || null;
      if (!ticket && form.ticket_no.trim()) {
        const r = await api.get("/repair-tickets", { params: { q: form.ticket_no.trim() } });
        const list = Array.isArray(r.data) ? r.data : r.data.items || [];
        const hit = list.find((t) => t.ticket_number?.toLowerCase() === form.ticket_no.trim().toLowerCase());
        if (!hit) { setBusy(false); return toast.error(`Ticket ${form.ticket_no} not found`); }
        ticket_id = hit.id;
      }
      const { data } = await api.post("/inbox/messages", { to_id: form.to_id, text: form.text, kind: form.kind, due_at: form.kind === "task" ? form.due_at || null : null, ticket_id });
      toast.success(form.kind === "task" ? "Task sent" : "Message sent");
      onSent?.(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="inbox-compose">
        <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between">
          <div className="text-[15px] font-bold text-slate-900">{form.kind === "task" ? "Give a task" : "New message"}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100" data-testid="inbox-compose-close"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          {isAdmin && (
            <div className="grid grid-cols-2 gap-1.5 p-1 rounded-xl bg-slate-100" data-testid="inbox-compose-kind">
              {[["message", "Message"], ["task", "Task"]].map(([k, l]) => (
                <button key={k} onClick={() => setForm((f) => ({ ...f, kind: k }))} data-testid={`inbox-compose-kind-${k}`}
                  className={`h-9 rounded-lg text-[13px] font-semibold transition-colors ${form.kind === k ? "bg-white text-[#1E5FEB] shadow-sm" : "text-slate-600"}`}>{l}</button>
              ))}
            </div>
          )}
          <div>
            <label className={LABEL}>To</label>
            <select value={form.to_id} onChange={set("to_id")} className={FIELD} data-testid="inbox-compose-to">
              <option value="">Choose a teammate…</option>
              {Object.entries(staff.reduce((g, u) => { (g[u.store_name || "Other"] = g[u.store_name || "Other"] || []).push(u); return g; }, {}))
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([store, people]) => (
                  <optgroup key={store} label={store}>
                    {people.map((u) => <option key={u.id} value={u.id}>{u.name}{u.role === "admin" ? " (admin)" : ""}</option>)}
                  </optgroup>
                ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={form.kind === "task" ? "" : "col-span-2"}>
              <label className={LABEL}>Ticket (optional)</label>
              {ticket
                ? <div className={`${FIELD} flex items-center font-mono text-slate-700 bg-slate-50`}>{ticket.ticket_number}</div>
                : <input value={form.ticket_no} onChange={set("ticket_no")} placeholder="e.g. R-00574" className={FIELD} data-testid="inbox-compose-ticket" />}
            </div>
            {form.kind === "task" && (
              <div>
                <label className={LABEL}>Due date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className={`${FIELD} flex items-center gap-2 text-left ${form.due_at ? "text-slate-900" : "text-slate-400"}`} data-testid="inbox-compose-due">
                      <CalendarBlank size={15} weight="bold" className="text-slate-400" />
                      {form.due_at ? fmtDue(form.due_at) : "No due date"}
                      {form.due_at && <X size={13} weight="bold" className="ml-auto text-slate-400 hover:text-slate-700" data-testid="inbox-compose-due-clear"
                        onClick={(e) => { e.stopPropagation(); setForm((f) => ({ ...f, due_at: "" })); }} />}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                    <Calendar mode="single" selected={form.due_at ? new Date(`${form.due_at}T00:00:00`) : undefined}
                      disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                      onSelect={(d) => setForm((f) => ({ ...f, due_at: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "" }))} />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
          <div>
            <label className={LABEL}>{form.kind === "task" ? "What needs doing" : "Message"}</label>
            <textarea rows={4} value={form.text} onChange={set("text")} data-testid="inbox-compose-text"
              placeholder={form.kind === "task" ? "e.g. Replace the screen and test Face ID before 3pm" : "Write your message…"}
              className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] text-[13.5px] focus:outline-none focus:border-[#1E5FEB] focus:ring-2 focus:ring-[#DBEAFE] resize-none" />
          </div>
        </div>
        <div className="flex gap-2 p-4 border-t border-[#E5E7EB] bg-[#FAFBFC]">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-[#E5E7EB] bg-white hover:bg-slate-50 text-[13px] font-semibold text-slate-700 transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="inbox-compose-send"
            className="flex-1 h-11 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:opacity-60 text-white text-[13px] font-bold inline-flex items-center justify-center gap-2 transition-colors">
            <PaperPlaneRight size={14} weight="fill" />{busy ? "Sending…" : form.kind === "task" ? "Send task" : "Send message"}
          </button>
        </div>
      </div>
    </div>
  );
}
